import { useState } from "react";

import debounce from "lodash/debounce";
import {
  MultiValue,
  components,
  InputProps,
  GroupBase,
  MultiValueProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";

import { useTagsQuery } from "@/graphql";

export interface TagItemInput {
  value: string;
  label: string;
  __isNew__?: boolean;
}

interface TagsInputProps {
  initValue: MultiValue<TagItemInput>;
  onChange: (tags: MultiValue<TagItemInput>) => void;
  menuPlacement?: "top" | "bottom" | "auto";
}

export function TagPill({
  tag,
  allowRemove = true,
  removeProps,
}: {
  tag: { id: string; name: string };
  allowRemove?: boolean;
  removeProps?: MultiValueProps<
    TagItemInput,
    true,
    GroupBase<TagItemInput>
  >["removeProps"];
}) {
  return (
    <div className="bg-light-blue flex w-fit items-center gap-2 rounded-sm px-3 py-1">
      <span className="text-main">{tag.name}</span>
      {allowRemove && (
        <div
          className="cursor-pointer rounded-md p-1 hover:bg-gray-300"
          {...removeProps}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.692 6L11.64 2.052C11.868 1.836 12 1.536 12 1.2C12 0.54 11.46 0 10.8 0C10.464 0 10.164 0.132 9.948 0.348L6 4.308L2.052 0.348C1.836 0.132 1.536 0 1.2 0C0.54 0 0 0.54 0 1.2C0 1.536 0.132 1.836 0.348 2.052L4.308 6L0.36 9.948C0.132 10.164 0 10.464 0 10.8C0 11.46 0.54 12 1.2 12C1.536 12 1.836 11.868 2.052 11.652L6 7.692L9.948 11.64C10.164 11.868 10.464 12 10.8 12C11.46 12 12 11.46 12 10.8C12 10.464 11.868 10.164 11.652 9.948L7.692 6Z"
              fill="#1478E3"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

function CustomInput(props: InputProps<TagItemInput, true>) {
  const { selectProps } = props;
  return (
    <components.Input
      {...props}
      onPaste={(e) => {
        const paste = e.clipboardData.getData("text");
        // Split by comma, semicolon, or newlines
        const items = paste
          .split(/[;,\n\r]+/)
          .map((item) => item.trim())
          .filter(Boolean);
        if (items.length > 1) {
          e.preventDefault();
          // Remove duplicates and already selected
          const selectedValues = (selectProps.value as TagItemInput[]).map(
            (t) => t.label,
          );
          const newTags = items
            .filter((item) => !selectedValues.includes(item))
            .map((item) => ({ value: item, label: item, __isNew__: true }));
          if (newTags.length > 0 && selectProps.onChange) {
            selectProps.onChange(
              [...(selectProps.value as TagItemInput[]), ...newTags],
              {
                action: "select-option",
                option: newTags[newTags.length - 1],
              },
            );
          }
        }
        if (props.onPaste) props.onPaste(e);
      }}
    />
  );
}

export function TagsInput({
  initValue,
  onChange,
  menuPlacement,
}: TagsInputProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MultiValue<TagItemInput>>(initValue);

  const { data, refetch } = useTagsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      query: search,
      page: 1,
      pageSize: 10,
    },
  });

  const onSearchChange = debounce((input: string) => {
    setSearch(input);
    refetch({
      query: input,
    });
  }, 200);

  const tags = data?.tags?.data || [];

  return (
    <div>
      <CreatableSelect
        menuPlacement={menuPlacement}
        formatCreateLabel={(input) => (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Create tags </span>
            {input}
          </div>
        )}
        components={{
          MultiValue: (props) => (
            <TagPill
              removeProps={props.removeProps}
              tag={{
                id: props.data.value,
                name: props.data.label,
              }}
            />
          ),
          DropdownIndicator: () => {
            return null;
          },
          IndicatorSeparator: () => {
            return null;
          },
          Input: CustomInput,
        }}
        styles={{
          control: (base) => ({
            ...base,
            padding: "4px 0px",
          }),
          valueContainer: (base) => ({
            ...base,
            display: "flex",
            gap: "8px",
          }),
          container: (base) => ({
            ...base,
            width: "100%",
            zIndex: 1000,
          }),
        }}
        isClearable={false}
        isMulti={true}
        closeMenuOnSelect={false}
        createOptionPosition={menuPlacement === "top" ? "last" : "first"}
        onInputChange={(input) => {
          onSearchChange(input);
        }}
        options={tags.map((tag) => ({
          value: tag?.id ?? "",
          label: tag?.name ?? "",
        }))}
        onChange={(value) => {
          setSelected(value);
          onChange(value);
        }}
        placeholder="Select a tag"
        value={selected}
      />
    </div>
  );
}
